import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { RouterLink } from "@angular/router";
import { Subject, Subscription, interval } from "rxjs";
import { debounceTime } from "rxjs/operators";
import Swal from "sweetalert2";

import { MantenimientoService } from "../mantenimiento.service";
import {
  DetalleMantenimientoComponent,
  DetalleMantenimientoData,
} from "../detalle-mantenimiento/detalle-mantenimiento.component";
import {
  Mantenimiento,
  MantenimientoReq,
  tipoMantenimiento,
  estatusDe,
  urgenciaDe,
} from "../../shared/model/mantenimiento/mantenimiento.model";

interface Filtro {
  valor: string;
  texto: string;
}

const ABIERTAS = ["SOLICITADA", "EN_ATENCION", "EN_ESPERA"];

/**
 * «Atención de mantenimiento»: la bandeja de quien atiende. Solo ve lo de
 * su empresa (lo decide el servidor con la sesión).
 *
 * Por omisión enseña las ABIERTAS, con lo que nadie ha tomado arriba y,
 * dentro de eso, lo urgente primero. Se refresca sola cada minuto, sin
 * velo de carga y sin tocar el reloj de inactividad (deFondo).
 */
@Component({
  selector: "app-gestion-mantenimiento",
  standalone: true,
  templateUrl: "./gestion-mantenimiento.component.html",
  styleUrls: ["../mantenimiento-comun.css", "./gestion-mantenimiento.component.css"],
  imports: [CommonModule, FormsModule, RouterLink, MatFormFieldModule, MatInputModule],
})
export class GestionMantenimientoComponent implements OnInit, OnDestroy {
  readonly FILTROS: Filtro[] = [
    { valor: "ABIERTAS", texto: "Abiertas" },
    { valor: "SOLICITADA", texto: "Sin tomar" },
    { valor: "EN_ATENCION", texto: "En atención" },
    { valor: "EN_ESPERA", texto: "En espera" },
    { valor: "RESUELTA", texto: "Por confirmar" },
    { valor: "CERRADA", texto: "Cerradas" },
    { valor: "CANCELADA", texto: "Canceladas" },
    { valor: "", texto: "Todas" },
  ];

  filtro = "ABIERTAS";
  soloMias = false;
  busca = "";

  lista: Mantenimiento[] = [];
  /** Los que atienden hoy en la empresa: para saber quién ya no. */
  responsables: Mantenimiento[] = [];
  conteos: Record<string, number> = {};
  total = 0;
  pagina = 1;
  readonly porPagina = 10;

  cargando = true;
  sinPermiso: string | null = null;
  tomando: number | null = null;

  private busqueda = new Subject<void>();
  private subs = new Subscription();

  constructor(
    private service: MantenimientoService,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.cargar();
    this.subs.add(this.busqueda.pipe(debounceTime(350)).subscribe(() => {
      this.pagina = 1;
      this.cargar();
    }));
    // Cada minuto, de fondo; no mientras hay un detalle abierto.
    this.subs.add(interval(60000).subscribe(() => {
      if (!this.dialog.openDialogs.length && !this.sinPermiso) {
        this.cargar(true);
      }
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  cargar(fondo = false): void {
    if (!fondo) {
      this.cargando = true;
    }
    const req: MantenimientoReq = {
      filtroEstatus: this.filtro,
      soloMias: this.soloMias,
      busca: this.busca.trim() || undefined,
      pagina: this.pagina,
      porPagina: this.porPagina,
    };
    this.service.bandeja(req, fondo).subscribe({
      next: (res) => {
        this.cargando = false;
        if (res?.codigoError) {
          if ((res.codigoError || "").startsWith("VAL-PERFIL")) {
            this.sinPermiso = res.mensajeError || "No tienes permiso para atender mantenimiento.";
          } else if (!fondo) {
            Swal.fire({ icon: "error", text: res.mensajeError || "No se pudo cargar la bandeja." });
          }
          this.cdr.detectChanges();
          return;
        }
        this.sinPermiso = null;
        this.lista = res.solicitudes || [];
        this.total = res.totalItems || 0;
        this.conteos = res.conteos || {};
        this.responsables = res.responsables || [];
        // Si la página se quedó vacía (alguien cerró lo último), a la anterior.
        if (!this.lista.length && this.pagina > 1) {
          this.pagina = Math.max(1, Math.ceil(this.total / this.porPagina));
          this.cargar(fondo);
          return;
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.cargando = false;
        if (!fondo) {
          Swal.fire({ icon: "error", text: "No se pudo conectar con el servidor." });
        }
        this.cdr.detectChanges();
      },
    });
  }

  /* ---------------- Filtros ---------------- */

  elegirFiltro(f: Filtro): void {
    if (this.filtro === f.valor) {
      return;
    }
    this.filtro = f.valor;
    this.pagina = 1;
    this.cargar();
  }

  alternarMias(): void {
    this.soloMias = !this.soloMias;
    this.pagina = 1;
    this.cargar();
  }

  alBuscar(): void {
    this.busqueda.next();
  }

  limpiarBusqueda(): void {
    this.busca = "";
    this.pagina = 1;
    this.cargar();
  }

  /** Cuántas hay en cada pastilla (del mismo universo: la empresa, y «mías» si está puesto). */
  conteo(f: Filtro): number {
    const n = (e: string) => this.conteos[e] || 0;
    if (f.valor === "ABIERTAS") {
      return ABIERTAS.reduce((a, e) => a + n(e), 0);
    }
    if (f.valor === "") {
      // «Todas» deja fuera las canceladas, igual que el servidor.
      return Object.keys(this.conteos).filter((e) => e !== "CANCELADA").reduce((a, e) => a + n(e), 0);
    }
    return n(f.valor);
  }

  /* ---------------- Acciones ---------------- */

  /** La tiene alguien que ya no atiende: cualquiera la puede relevar. */
  huerfana(s: Mantenimiento): boolean {
    const r = s.idUsuarioResponsable;
    return r != null && !this.responsables.some((x) => x.idUsuarioResponsable === r);
  }

  puedeTomar(s: Mantenimiento): boolean {
    return ABIERTAS.includes(s.estatus || "") && (s.idUsuarioResponsable == null || this.huerfana(s));
  }

  tomar(s: Mantenimiento, ev: Event): void {
    ev.stopPropagation();
    if (!s.idSolicitud || this.tomando) {
      return;
    }
    this.tomando = s.idSolicitud;
    this.service.tomar(s.idSolicitud).subscribe({
      next: (res) => {
        this.tomando = null;
        if (res?.codigoError) {
          Swal.fire({ icon: "error", text: res.mensajeError || "No se pudo tomar." });
        } else {
          Swal.fire({ icon: "success", text: `${s.folio} es tuya: está en atención.`, timer: 1600, showConfirmButton: false });
        }
        this.cargar(true);
      },
      error: () => {
        this.tomando = null;
        Swal.fire({ icon: "error", text: "No se pudo conectar con el servidor." });
        this.cdr.detectChanges();
      },
    });
  }

  abrir(s: Mantenimiento): void {
    if (!s.idSolicitud) {
      return;
    }
    const ref = this.dialog.open<DetalleMantenimientoComponent, DetalleMantenimientoData, boolean>(
      DetalleMantenimientoComponent,
      {
        data: { idSolicitud: s.idSolicitud },
        panelClass: "app-standard-dialog",
        backdropClass: "app-standard-dialog-backdrop",
        width: "940px",
        maxWidth: "96vw",
        autoFocus: false,
      },
    );
    ref.afterClosed().subscribe((cambio) => {
      if (cambio) {
        this.cargar(true);
      }
    });
  }

  /* ---------------- Lo que se ve ---------------- */

  cat(s: Mantenimiento) {
    return tipoMantenimiento(s.categoria);
  }

  est(s: Mantenimiento) {
    return estatusDe(s.estatus);
  }

  urg(s: Mantenimiento) {
    return urgenciaDe(s.urgencia);
  }

  /** «2026-09-29 10:15» → «29 sep, 10:15». */
  cuando(f?: string | null): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(f || "");
    if (!m) {
      return f || "";
    }
    const d = new Date(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0));
    const dia = d.toLocaleDateString("es-MX", { day: "numeric", month: "short" }).replace(".", "");
    return m[4] ? `${dia}, ${m[4]}:${m[5]}` : dia;
  }

  trackSol(_: number, s: Mantenimiento): number | undefined {
    return s.idSolicitud;
  }

  /* ---------------- Paginado (en el servidor) ---------------- */

  totalPaginas(): number {
    return Math.max(1, Math.ceil(this.total / this.porPagina));
  }

  irPagina(p: number): void {
    const destino = Math.min(Math.max(1, p), this.totalPaginas());
    if (destino !== this.pagina) {
      this.pagina = destino;
      this.cargar();
    }
  }

  paginas(): (number | null)[] {
    const t = this.totalPaginas();
    if (t <= 7) {
      return Array.from({ length: t }, (_, i) => i + 1);
    }
    const p = this.pagina;
    const salida: (number | null)[] = [1];
    if (p > 3) salida.push(null);
    for (let i = Math.max(2, p - 1); i <= Math.min(t - 1, p + 1); i++) salida.push(i);
    if (p < t - 2) salida.push(null);
    salida.push(t);
    return salida;
  }

  rangoInicio(): number {
    return this.total ? (this.pagina - 1) * this.porPagina + 1 : 0;
  }

  rangoFin(): number {
    return Math.min(this.pagina * this.porPagina, this.total);
  }
}
